import React, { useState, useEffect, useCallback } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  NativeSelect,
  Textarea,
  SimpleGrid,
  Checkbox,
  Link,
  Spinner,
  Icon,
  Field,
} from '@chakra-ui/react';
import { Switch } from '@/components/ui/chakra-v2-compat';
import { toast } from '@/components/ui/toaster';
import { InputGroup, InputLeftElement, InputRightElement } from '@/components/ui/input-group';
import { FaMapMarkerAlt, FaExternalLinkAlt, FaBus } from 'react-icons/fa';
import ActuacionMapPreview from './ActuacionMapPreview.jsx';
import { useTiposFormacionCatalogo } from '../hooks/useTiposFormacionCatalogo.js';

/**
 * Campos de ficha de actuación (misma UX que editar en ActuacionDetalle).
 * @param {object} props
 * @param {Record<string, unknown>} props.form
 * @param {React.Dispatch<React.SetStateAction<object>>} props.setForm
 * @param {Array<{ id: string, nombre: string }>} props.plantillas
 * @param {import('@chakra-ui/react').CreateToastFnReturn} props.toast
 * @param {boolean} props.enabled — si false, no se dispara Nominatim (p. ej. modal cerrado).
 * @param {boolean} props.ubicLlegadaDistinta
 * @param {React.Dispatch<React.SetStateAction<boolean>>} props.setUbicLlegadaDistinta
 * @param {number} [props.searchResetKey] — al incrementar, reinicia flags de autocompletado de ubicación.
 * @param {string} [props.fechaMinYMD] — si se indica (YYYY-MM-DD), el input de fecha no permite días anteriores (p. ej. alta de actuación).
 * @param {1|2|3|'all'} [props.wizardStep] — en el asistente de alta, muestra solo el bloque del paso indicado.
 */
export default function ActuacionDatosFormFields({
  form,
  setForm,
  plantillas,
  toast,
  enabled,
  ubicLlegadaDistinta,
  setUbicLlegadaDistinta,
  searchResetKey = 0,
  fechaMinYMD,
  wizardStep = 'all',
}) {
  const showStep = (n) => wizardStep === 'all' || wizardStep === n;
  const { tipos: tiposFormacionCatalogo } = useTiposFormacionCatalogo();
  const defaultTipoCatalog = tiposFormacionCatalogo[0]?.codigo || 'PROCESION';
  const tipoSelectValue =
    form.tipo && tiposFormacionCatalogo.some((t) => String(t.codigo) === String(form.tipo))
      ? form.tipo
      : form.tipo || defaultTipoCatalog;

  const cardBg = useColorModeValue('white', 'gray.800');
  const ubicSuggestBorder = useColorModeValue('gray.200', 'gray.600');
  const ubicSuggestHoverBg = useColorModeValue('gray.50', 'gray.700');

  const [ubicInicioSuggestions, setUbicInicioSuggestions] = useState([]);
  const [ubicInicioShow, setUbicInicioShow] = useState(false);
  const [ubicInicioSearching, setUbicInicioSearching] = useState(false);
  const [ubicInicioUserEdited, setUbicInicioUserEdited] = useState(false);

  const [ubicFinSuggestions, setUbicFinSuggestions] = useState([]);
  const [ubicFinShow, setUbicFinShow] = useState(false);
  const [ubicFinSearching, setUbicFinSearching] = useState(false);
  const [ubicFinUserEdited, setUbicFinUserEdited] = useState(false);

  const [autobusSuggestions, setAutobusSuggestions] = useState([]);
  const [autobusShow, setAutobusShow] = useState(false);
  const [autobusSearching, setAutobusSearching] = useState(false);
  const [autobusUserEdited, setAutobusUserEdited] = useState(false);

  useEffect(() => {
    setUbicInicioUserEdited(false);
    setUbicFinUserEdited(false);
    setAutobusUserEdited(false);
    setUbicInicioSuggestions([]);
    setUbicFinSuggestions([]);
    setAutobusSuggestions([]);
    setUbicInicioShow(false);
    setUbicFinShow(false);
    setAutobusShow(false);
  }, [searchResetKey]);

  const selectUbicacionInicio = useCallback(
    (loc) => {
      const la = parseFloat(loc.lat);
      const lo = parseFloat(loc.lon);
      if (Number.isNaN(la) || Number.isNaN(lo)) {
        toast({
          title: 'Ubicación sin coordenadas',
          description: 'Prueba con una dirección más concreta (calle, número, ciudad).',
          status: 'warning',
          duration: 4000,
        });
        return;
      }
      setUbicInicioShow(false);
      setUbicInicioSuggestions([]);
      setForm((f) => ({
        ...f,
        ubicacion: loc.display_name,
        lat: la,
        lng: lo,
      }));
    },
    [toast, setForm],
  );

  const selectUbicacionFin = useCallback(
    (loc) => {
      const la = parseFloat(loc.lat);
      const lo = parseFloat(loc.lon);
      if (Number.isNaN(la) || Number.isNaN(lo)) {
        toast({
          title: 'Ubicación sin coordenadas',
          description: 'Prueba con una dirección más concreta (calle, número, ciudad).',
          status: 'warning',
          duration: 4000,
        });
        return;
      }
      setUbicFinShow(false);
      setUbicFinSuggestions([]);
      setForm((f) => ({
        ...f,
        ubicacionFin: loc.display_name,
        latFin: la,
        lngFin: lo,
      }));
    },
    [toast, setForm],
  );

  const selectAutobusUbicacion = useCallback(
    (loc) => {
      const la = parseFloat(loc.lat);
      const lo = parseFloat(loc.lon);
      if (Number.isNaN(la) || Number.isNaN(lo)) {
        toast({
          title: 'Ubicación sin coordenadas',
          description: 'Prueba con una dirección más concreta (calle, número, ciudad).',
          status: 'warning',
          duration: 4000,
        });
        return;
      }
      setAutobusShow(false);
      setAutobusSuggestions([]);
      setForm((f) => ({
        ...f,
        autobusUbicacion: loc.display_name,
        autobusLat: la,
        autobusLng: lo,
      }));
    },
    [toast, setForm],
  );

  useEffect(() => {
    if (!enabled) {
      setUbicInicioShow(false);
      setUbicInicioUserEdited(false);
      setUbicFinUserEdited(false);
      setAutobusUserEdited(false);
      return;
    }
    if (!ubicInicioUserEdited) {
      setUbicInicioSuggestions([]);
      setUbicInicioShow(false);
      return;
    }
    const q = (form.ubicacion || '').trim();
    if (q.length < 3) {
      setUbicInicioSuggestions([]);
      setUbicInicioShow(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setUbicInicioSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5&countrycodes=es&addressdetails=1`,
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setUbicInicioSuggestions(list);
        setUbicInicioShow(list.length > 0);
      } catch {
        if (!cancelled) setUbicInicioSuggestions([]);
      } finally {
        if (!cancelled) setUbicInicioSearching(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form.ubicacion, enabled, ubicInicioUserEdited]);

  useEffect(() => {
    if (!enabled || !ubicLlegadaDistinta) {
      setUbicFinShow(false);
      if (!ubicLlegadaDistinta) setUbicFinSuggestions([]);
      return;
    }
    if (!ubicFinUserEdited) {
      setUbicFinSuggestions([]);
      setUbicFinShow(false);
      return;
    }
    const q = (form.ubicacionFin || '').trim();
    if (q.length < 3) {
      setUbicFinSuggestions([]);
      setUbicFinShow(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setUbicFinSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5&countrycodes=es&addressdetails=1`,
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setUbicFinSuggestions(list);
        setUbicFinShow(list.length > 0);
      } catch {
        if (!cancelled) setUbicFinSuggestions([]);
      } finally {
        if (!cancelled) setUbicFinSearching(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form.ubicacionFin, enabled, ubicLlegadaDistinta, ubicFinUserEdited]);

  useEffect(() => {
    if (!enabled || !form.necesidadAutobus) {
      setAutobusShow(false);
      if (!form.necesidadAutobus) setAutobusSuggestions([]);
      return;
    }
    if (!autobusUserEdited) {
      setAutobusSuggestions([]);
      setAutobusShow(false);
      return;
    }
    const q = (form.autobusUbicacion || '').trim();
    if (q.length < 3) {
      setAutobusSuggestions([]);
      setAutobusShow(false);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setAutobusSearching(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5&countrycodes=es&addressdetails=1`,
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setAutobusSuggestions(list);
        setAutobusShow(list.length > 0);
      } catch {
        if (!cancelled) setAutobusSuggestions([]);
      } finally {
        if (!cancelled) setAutobusSearching(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form.autobusUbicacion, enabled, form.necesidadAutobus, autobusUserEdited]);

  const mapPreview = (() => {
    if (!showStep(2) && !showStep(3) && wizardStep !== 'all') return null;
    const hasIni =
      form.lat != null && form.lng != null && !Number.isNaN(form.lat) && !Number.isNaN(form.lng);
    const hasFin =
      ubicLlegadaDistinta &&
      form.latFin != null &&
      form.lngFin != null &&
      !Number.isNaN(form.latFin) &&
      !Number.isNaN(form.lngFin);
    const hasBus =
      Boolean(form.necesidadAutobus) &&
      form.autobusLat != null &&
      form.autobusLng != null &&
      !Number.isNaN(form.autobusLat) &&
      !Number.isNaN(form.autobusLng);
    if (!hasIni && !hasFin && !hasBus) return null;
    return (
      <Box rounded="md" overflow="hidden" borderWidth="1px" borderColor={ubicSuggestBorder}>
        <ActuacionMapPreview
          lat={form.lat}
          lng={form.lng}
          latFin={ubicLlegadaDistinta ? form.latFin : null}
          lngFin={ubicLlegadaDistinta ? form.lngFin : null}
          latAutobus={form.necesidadAutobus ? form.autobusLat : null}
          lngAutobus={form.necesidadAutobus ? form.autobusLng : null}
          height="240px"
        />
        <Box px={2} py={1} bg="gray.50" _dark={{ bg: 'gray.900' }}>
          <HStack gap={4} flexWrap="wrap">
            {hasIni ? (
              <Link
                href={`https://www.openstreetmap.org/?mlat=${form.lat}&mlon=${form.lng}#map=16/${form.lat}/${form.lng}`}
                fontSize="xs"
                color="purple.600"
                target='_blank'
                rel='noopener noreferrer'>
                Inicio en OSM <Icon asChild><FaExternalLinkAlt /></Icon>
              </Link>
            ) : null}
            {hasFin ? (
              <Link
                href={`https://www.openstreetmap.org/?mlat=${form.latFin}&mlon=${form.lngFin}#map=16/${form.latFin}/${form.lngFin}`}
                fontSize="xs"
                color="red.600"
                target='_blank'
                rel='noopener noreferrer'>
                Llegada en OSM <Icon asChild><FaExternalLinkAlt /></Icon>
              </Link>
            ) : null}
            {hasBus ? (
              <Link
                href={`https://www.openstreetmap.org/?mlat=${form.autobusLat}&mlon=${form.autobusLng}#map=16/${form.autobusLat}/${form.autobusLng}`}
                fontSize="xs"
                color="blue.600"
                target='_blank'
                rel='noopener noreferrer'>
                Autobús en OSM <Icon asChild><FaExternalLinkAlt /></Icon>
              </Link>
            ) : null}
          </HStack>
        </Box>
      </Box>
    );
  })();

  return (
    <VStack gap={3} align="stretch">
      {showStep(1) ? (
        <>
      <Field.Root>
        <Field.Label fontSize="sm">Nombre</Field.Label>
        <Input
          size="sm"
          value={form.nombre || ''}
          onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
        />
      </Field.Root>
      <Field.Root>
        <Field.Label fontSize="sm">Descripción</Field.Label>
        <Textarea
          size="sm"
          value={form.descripcion || ''}
          onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))}
          rows={3}
        />
      </Field.Root>
      <Field.Root>
        <Field.Label fontSize="sm">Tipo</Field.Label>
        <NativeSelect.Root>
          <NativeSelect.Field
            size="sm"
            value={tipoSelectValue}
            onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))}>
            {tiposFormacionCatalogo.map((t) => (
              <option key={t.codigo} value={t.codigo}>
                {t.nombre}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>
      <Field.Root>
        <Field.Label fontSize="sm">Plantilla</Field.Label>
        <NativeSelect.Root>
          <NativeSelect.Field
            size="sm"
            value={form.plantillaId || ''}
            onChange={(e) => setForm((f) => ({ ...f, plantillaId: e.target.value }))}
            placeholder="Sin plantilla">
            {plantillas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
      </Field.Root>
        </>
      ) : null}
      {showStep(2) ? (
        <>
      <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
        <Field.Root>
          <Field.Label fontSize="sm">Fecha</Field.Label>
          <Input
            size="sm"
            type="date"
            min={fechaMinYMD || undefined}
            value={form.fecha || ''}
            onChange={(e) => setForm((f) => ({ ...f, fecha: e.target.value }))}
          />
        </Field.Root>
        <Field.Root>
          <Field.Label fontSize="sm">Hora inicio</Field.Label>
          <Input
            size="sm"
            type="time"
            value={form.horaInicio || ''}
            onChange={(e) => setForm((f) => ({ ...f, horaInicio: e.target.value }))}
          />
        </Field.Root>
        <Field.Root>
          <Field.Label fontSize="sm">Hora fin</Field.Label>
          <Input
            size="sm"
            type="time"
            value={form.horaFin || ''}
            onChange={(e) => setForm((f) => ({ ...f, horaFin: e.target.value }))}
          />
        </Field.Root>
      </SimpleGrid>
      <Field.Root w="full">
        <Field.Label fontSize="sm">Lugar de inicio (salida)</Field.Label>
        <Box position="relative" w="full">
          <InputGroup size="md" w="full">
            <InputLeftElement pointerEvents="none">
              <Icon color="purple.500" asChild><FaMapMarkerAlt /></Icon>
            </InputLeftElement>
            <Input
              w="full"
              minW={0}
              value={form.ubicacion || ''}
              onChange={(e) => {
                const v = e.target.value;
                setUbicInicioUserEdited(true);
                setForm((f) => ({ ...f, ubicacion: v, lat: null, lng: null }));
              }}
              onFocus={() => ubicInicioSuggestions.length > 0 && setUbicInicioShow(true)}
              placeholder="Escribe y elige una sugerencia para fijar el mapa"
              title={form.ubicacion || undefined}
            />
            {ubicInicioSearching ? (
              <InputRightElement>
                <Spinner size="sm" color="purple.500" />
              </InputRightElement>
            ) : null}
          </InputGroup>
          {ubicInicioShow && ubicInicioSuggestions.length > 0 ? (
            <Box
              position="absolute"
              top="100%"
              left={0}
              right={0}
              zIndex={20}
              mt={1}
              bg={cardBg}
              borderWidth="1px"
              borderColor={ubicSuggestBorder}
              rounded="md"
              shadow="lg"
              maxH="240px"
              overflowY="auto"
            >
              {ubicInicioSuggestions.map((s, idx) => (
                <Box
                  key={`inicio-${s.lat}-${s.lon}-${idx}`}
                  px={3}
                  py={2}
                  cursor="pointer"
                  borderBottomWidth={idx < ubicInicioSuggestions.length - 1 ? '1px' : '0'}
                  borderColor={ubicSuggestBorder}
                  _hover={{ bg: ubicSuggestHoverBg }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => selectUbicacionInicio(s)}
                >
                  <HStack align="start" gap={2}>
                    <Icon color="purple.500" mt={0.5} flexShrink={0} asChild><FaMapMarkerAlt /></Icon>
                    <Text fontSize="sm">{s.display_name}</Text>
                  </HStack>
                </Box>
              ))}
            </Box>
          ) : null}
        </Box>
        <Field.HelperText fontSize="xs">
          Búsqueda OpenStreetMap (España). Elige una fila para guardar coordenadas del inicio.
        </Field.HelperText>
      </Field.Root>
      <Field.Root display="flex" alignItems="center">
        <Checkbox.Root
          onCheckedChange={(details) => {
            const on = details.checked === true;
            setUbicLlegadaDistinta(on);
            if (!on) {
              setForm((f) => ({ ...f, ubicacionFin: '', latFin: null, lngFin: null }));
              setUbicFinSuggestions([]);
              setUbicFinShow(false);
              setUbicFinUserEdited(false);
            }
          }}
          colorPalette="purple"
          checked={ubicLlegadaDistinta}
        ><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
          <Text fontSize="sm">El lugar de fin es distinto del de inicio</Text>
        </Checkbox.Label></Checkbox.Root>
      </Field.Root>
      {ubicLlegadaDistinta ? (
        <Field.Root w="full">
          <Field.Label fontSize="sm">Lugar de llegada (fin)</Field.Label>
          <Box position="relative" w="full">
            <InputGroup size="md" w="full">
              <InputLeftElement pointerEvents="none">
                <Icon color="red.400" asChild><FaMapMarkerAlt /></Icon>
              </InputLeftElement>
              <Input
                w="full"
                minW={0}
                value={form.ubicacionFin || ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setUbicFinUserEdited(true);
                  setForm((f) => ({ ...f, ubicacionFin: v, latFin: null, lngFin: null }));
                }}
                onFocus={() => ubicFinSuggestions.length > 0 && setUbicFinShow(true)}
                placeholder="Busca y elige el punto de llegada"
                title={form.ubicacionFin || undefined}
              />
              {ubicFinSearching ? (
                <InputRightElement>
                  <Spinner size="sm" color="purple.500" />
                </InputRightElement>
              ) : null}
            </InputGroup>
            {ubicFinShow && ubicFinSuggestions.length > 0 ? (
              <Box
                position="absolute"
                top="100%"
                left={0}
                right={0}
                zIndex={21}
                mt={1}
                bg={cardBg}
                borderWidth="1px"
                borderColor={ubicSuggestBorder}
                rounded="md"
                shadow="lg"
                maxH="240px"
                overflowY="auto"
              >
                {ubicFinSuggestions.map((s, idx) => (
                  <Box
                    key={`fin-${s.lat}-${s.lon}-${idx}`}
                    px={3}
                    py={2}
                    cursor="pointer"
                    borderBottomWidth={idx < ubicFinSuggestions.length - 1 ? '1px' : '0'}
                    borderColor={ubicSuggestBorder}
                    _hover={{ bg: ubicSuggestHoverBg }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectUbicacionFin(s)}
                  >
                    <HStack align="start" gap={2}>
                      <Icon color="red.400" mt={0.5} flexShrink={0} asChild><FaMapMarkerAlt /></Icon>
                      <Text fontSize="sm">{s.display_name}</Text>
                    </HStack>
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>
          <Field.HelperText fontSize="xs">
            Opcional: mismo flujo que el inicio. En el mapa verás inicio (morado), llegada (rojo) y la ruta entre
            ambos.
          </Field.HelperText>
        </Field.Root>
      ) : null}
      {mapPreview}
        </>
      ) : null}
      {showStep(3) ? (
        <>
      <Field.Root>
        <Field.Label fontSize="sm">Uniforme</Field.Label>
        <Input
          size="sm"
          value={form.uniforme || ''}
          onChange={(e) => setForm((f) => ({ ...f, uniforme: e.target.value }))}
          placeholder="Ej: Gala, túnica, traje corto…"
        />
      </Field.Root>
      <Field.Root>
        <Field.Label fontSize="sm">Enlace partituras</Field.Label>
        <Input
          size="sm"
          value={form.enlacePartituras || ''}
          onChange={(e) => setForm((f) => ({ ...f, enlacePartituras: e.target.value }))}
          placeholder="https://drive.google.com/..."
        />
      </Field.Root>
      <Field.Root>
        <Field.Label fontSize="sm">Observaciones</Field.Label>
        <Textarea
          size="sm"
          value={form.observaciones || ''}
          onChange={(e) => setForm((f) => ({ ...f, observaciones: e.target.value }))}
          rows={3}
        />
      </Field.Root>
      <Field.Root display="flex" alignItems="center">
        <Field.Label fontSize="sm" mb={0}>
          Preguntar por autobús
        </Field.Label>
        <Switch
          checked={Boolean(form.necesidadAutobus)}
          onChange={(e) => {
            const on = e.target.checked;
            setForm((f) => ({
              ...f,
              necesidadAutobus: on,
              ...(!on
                ? {
                    autobusUbicacion: '',
                    autobusHoraSalida: '',
                    autobusLat: null,
                    autobusLng: null,
                  }
                : {}),
            }));
          }}
          colorPalette="purple"
        />
      </Field.Root>
      {form.necesidadAutobus ? (
        <>
          <Field.Root required w="full">
            <Field.Label fontSize="sm">Ubicación del autobús (recogida)</Field.Label>
            <Box position="relative" w="full">
              <InputGroup size="md" w="full">
                <InputLeftElement pointerEvents="none">
                  <Icon color="blue.600" asChild><FaBus /></Icon>
                </InputLeftElement>
                <Input
                  w="full"
                  minW={0}
                  value={form.autobusUbicacion || ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAutobusUserEdited(true);
                    setForm((f) => ({
                      ...f,
                      autobusUbicacion: v,
                      autobusLat: null,
                      autobusLng: null,
                    }));
                  }}
                  onFocus={() => autobusSuggestions.length > 0 && setAutobusShow(true)}
                  placeholder="Busca y elige el punto de recogida del autobús"
                  title={form.autobusUbicacion || undefined}
                />
                {autobusSearching ? (
                  <InputRightElement>
                    <Spinner size="sm" color="blue.500" />
                  </InputRightElement>
                ) : null}
              </InputGroup>
              {autobusShow && autobusSuggestions.length > 0 ? (
                <Box
                  position="absolute"
                  top="100%"
                  left={0}
                  right={0}
                  zIndex={21}
                  mt={1}
                  bg={cardBg}
                  borderWidth="1px"
                  borderColor={ubicSuggestBorder}
                  rounded="md"
                  shadow="lg"
                  maxH="240px"
                  overflowY="auto"
                >
                  {autobusSuggestions.map((s, idx) => (
                    <Box
                      key={`bus-${s.lat}-${s.lon}-${idx}`}
                      px={3}
                      py={2}
                      cursor="pointer"
                      borderBottomWidth={idx < autobusSuggestions.length - 1 ? '1px' : '0'}
                      borderColor={ubicSuggestBorder}
                      _hover={{ bg: ubicSuggestHoverBg }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => selectAutobusUbicacion(s)}
                    >
                      <HStack align="start" gap={2}>
                        <Icon color="blue.600" mt={0.5} flexShrink={0} asChild><FaBus /></Icon>
                        <Text fontSize="sm">{s.display_name}</Text>
                      </HStack>
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Box>
            <Field.HelperText fontSize="xs">
              Mismo buscador que inicio y llegada: elige una sugerencia para fijar coordenadas en el mapa.
            </Field.HelperText>
          </Field.Root>
          <Field.Root required>
            <Field.Label fontSize="sm">Hora de salida del autobús</Field.Label>
            <Input
              size="sm"
              type="time"
              value={form.autobusHoraSalida || ''}
              onChange={(e) => setForm((f) => ({ ...f, autobusHoraSalida: e.target.value }))}
            />
            <Field.HelperText fontSize="xs">Hora prevista de salida del vehículo.</Field.HelperText>
          </Field.Root>
        </>
      ) : null}
      {showStep(3) ? mapPreview : null}
        </>
      ) : null}
    </VStack>
  );
}
